import { FunctionalComponent, h, JSX } from 'preact';
import { MutableRef, useEffect, useRef, useState } from 'preact/hooks';
import { ChevronRightIcon } from '@uw/icons';
import { onKeyboardSelection } from '../../helpers';
import './expand-panel.scss';

interface ExpandPanelProps {
  title: string;
  renderRight?: () => JSX.Element,
  icon?: JSX.Element;
  control?: JSX.Element;
  /*
   * Values for controlled component
   * */
  isOpen?: boolean;
  scrollDown?: boolean;
  innerRef?: MutableRef<HTMLButtonElement>;
  onToggle?: (value?: MouseEvent | KeyboardEvent) => void;
  readerText?: string;
  readerLang?: string;
}

export const ExpandPanel: FunctionalComponent<ExpandPanelProps> = (props) => {
  const {
    icon,
    title,
    control,
    isOpen = false,
    onToggle,
    innerRef = null,
    scrollDown = false,
    readerText = '',
    readerLang = '',
    children,
    renderRight,
  } = props;
  const [isExpanded, setIsExpanded] = useState(isOpen);

  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsExpanded(isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (scrollDown && container.current && isExpanded) {
      // we need scrollIntoView to scroll to expanded content of ExpandPanel component
      container.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  }, [isExpanded]);

  const toggleExpand = (event?: MouseEvent | KeyboardEvent) => {
    if (onToggle) {
      return onToggle(event);
    }

    setIsExpanded((value) => !value);
  };

  return (
    <div className="uwaw-option" ref={container}>
      <div className="uwaw-option__i">
        <button
          type="button"
          className="uwaw-option__drop-btn"
          aria-expanded={isExpanded}
          onClick={toggleExpand}
          onKeyDown={onKeyboardSelection(toggleExpand)}
          ref={innerRef}
          data-uw-reader-content={readerText}
          data-uw-reader-language={readerLang}
        >
          <span className="expanded_panel__wrapper">
            <span className="expanded_panel__wrapper__icon">{icon}</span>
            {title}
          </span>
          <span className="uwaw-option__drop-icon">
            <ChevronRightIcon />
          </span>
        </button>
        {control}
        {renderRight && renderRight()}
      </div>
      {isExpanded && <div className="uwaw-option__dropdown">{children}</div>}
    </div>
  );
};
